// ./src/hooks/useExpenseSchedules.ts
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import {
  ExpenseSchedule,
  ExpenseScheduleService,
  ExpenseScheduleInput,
  ExpenseScheduleFilter,
  ExpenseScheduleRunNowResult,
  EXPENSE_SCHEDULE_STATUS,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";
import { Pagination, SortItem } from "@/utils/api/commonApiTypes";

// ============================================
// Types
// ============================================

export interface UseExpenseSchedulesOptions {
  /**
   * Page size for pagination.
   * Mount-time configuration — changing after mount has no effect.
   */
  pageSize?: number;

  /**
   * Filters applied on initial autoLoad and userId-change reloads.
   * Mount-time configuration — call list(newFilters) to change.
   */
  initialFilter?: ExpenseScheduleFilter;

  /**
   * Sort applied on initial autoLoad and userId-change reloads.
   * Mount-time configuration — pass sort to list() to change.
   */
  initialSortBy?: SortItem[];

  /** Auto-load data on mount (and on userId change) */
  autoLoad?: boolean;
}

export interface UseExpenseSchedulesReturn {
  // Data state
  schedule: ExpenseSchedule | null;
  schedules: ExpenseSchedule[];
  isLoading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  // Pagination state
  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingId: string | null;
  isPausing: boolean;
  isResuming: boolean;
  isRunningNow: boolean;

  // Filter and sort
  filter: ExpenseScheduleFilter;
  sortBy: SortItem[];

  // Core operations
  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: ExpenseScheduleFilter,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: ExpenseScheduleInput) => Promise<OpResult>;
  update: (id: string, params: ExpenseScheduleInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  // Custom operations
  pause: (id: string) => Promise<OpResult>;
  resume: (id: string) => Promise<OpResult>;
  runNow: (id: string) => Promise<ExpenseScheduleRunNowResult>;

  // Convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedSchedule: (schedule: ExpenseSchedule | null) => void;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  getScheduleById: (id: string | undefined) => ExpenseSchedule | undefined;

  // Derived data helpers
  activeSchedules: ExpenseSchedule[];
  pausedSchedules: ExpenseSchedule[];
  completedSchedules: ExpenseSchedule[];

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_PAGE_SIZE = 50;

const DEFAULT_SORT_BY: SortItem[] = [
  { name: "nextScheduledAt", order: "ASC" },
];

const DEFAULT_FILTER: ExpenseScheduleFilter = {};

// ============================================
// Hook Implementation
// ============================================

export const useExpenseSchedules = (
  options: UseExpenseSchedulesOptions = {}
): UseExpenseSchedulesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilter = DEFAULT_FILTER,
    initialSortBy = DEFAULT_SORT_BY,
    autoLoad = false,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // ── Mount-time configuration captured in refs ──
  const pageSizeRef = useRef(pageSize);
  const initialFilterRef = useRef(initialFilter);
  const initialSortByRef = useRef(initialSortBy);

  // Ref to track the last-queried filters for loadMore/refresh
  const lastQueriedFilterRef = useRef<ExpenseScheduleFilter>(initialFilter);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSortBy);

  // Monotonically increasing call ID to detect stale list() responses
  const listCallIdRef = useRef(0);

  // Track previous userId for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Data state
  const [schedule, setSchedule] = useState<ExpenseSchedule | null>(null);
  const [schedules, setSchedules] = useState<ExpenseSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters — tracks what was last queried
  const [filter, setFilter] = useState<ExpenseScheduleFilter>(initialFilter);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSortBy);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isRunningNow, setIsRunningNow] = useState(false);

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && schedules.length === 0,
    [isLoaded, schedules.length]
  );

  // ==========================================
  // List Helpers
  // ==========================================

  const updateOrAddInList = useCallback((updatedItem: ExpenseSchedule) => {
    setSchedules((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === updatedItem.id);
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  const removeFromList = useCallback((id: string) => {
    setSchedules((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setSchedules((prev) => prev.filter((item) => !idsSet.has(item.id || "")));
  }, []);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;
    setSchedule(null);
    setSchedules([]);
    setLastError(null);
    setIsLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingId(null);
    setIsPausing(false);
    setIsResuming(false);
    setIsRunningNow(false);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  const get = useCallback(
    async (id: string): Promise<OpResult> => {
      setIsLoading(true);
      setLastError(null);

      try {
        const result = await ExpenseScheduleService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as ExpenseSchedule[])[0];
          setSchedule(data);
          updateOrAddInList(data);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load expense schedule"));
        setSchedule(null);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load expense schedule"));
        setSchedule(null);
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [updateOrAddInList]
  );

  /**
   * List expense schedules.
   * Race-condition safe via listCallIdRef.
   */
  const list = useCallback(
    async (
      filterOverride: ExpenseScheduleFilter = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setIsLoading(true);
        setCurrentPage(1);
      }

      setLastError(null);

      // Store queried filters/sort in refs
      setFilter(filterOverride);
      lastQueriedFilterRef.current = filterOverride;
      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const result = await ExpenseScheduleService.list(
          filterOverride,
          paginationParams,
          currentSortBy
        );

        // Guard stale responses
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as ExpenseSchedule[])
            : [];

          if (isAppending) {
            setSchedules((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setSchedules(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load expense schedules"));
        setIsLoaded(true);
        if (!isAppending) setSchedules([]);
        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load expense schedules"));
        setIsLoaded(true);
        if (!isAppending) setSchedules([]);
        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setIsLoading(false);
          }
        }
      }
    },
    []
  );

  const getMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) return new OpResult();

      setIsLoading(true);
      setLastError(null);

      try {
        const result = await ExpenseScheduleService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as ExpenseSchedule[])
            : [];
          loadedItems.forEach((item) => updateOrAddInList(item));
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load expense schedules"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load expense schedules"));
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // ==========================================
  // Mutation Operations
  // ==========================================

  const create = useCallback(
    async (params: ExpenseScheduleInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await ExpenseScheduleService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newSchedule = (result.data as ExpenseSchedule[])[0];
          setSchedule(newSchedule);
          setSchedules((prev) => [newSchedule, ...prev]);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to create expense schedule"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to create expense schedule"));
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  const update = useCallback(
    async (id: string, params: ExpenseScheduleInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Schedule ID is required for updating"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await ExpenseScheduleService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedSchedule = (result.data as ExpenseSchedule[])[0];
          setSchedule(updatedSchedule);
          updateOrAddInList(updatedSchedule);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to update expense schedule"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update expense schedule"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Schedule ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await ExpenseScheduleService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setSchedule(null);
          removeFromList(id);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove expense schedule"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove expense schedule"));
        return result;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one schedule ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setLastError(null);

      try {
        const result = await ExpenseScheduleService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setSchedule(null);
          removeManyFromList(ids);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove expense schedules"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove expense schedules"));
        return result;
      }
    },
    [removeManyFromList]
  );

  // ==========================================
  // Custom Operations
  // ==========================================

  const pause = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Schedule ID is required");
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsPausing(true);
      setLastError(null);

      try {
        const result = await ExpenseScheduleService.pause(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedSchedule = (result.data as ExpenseSchedule[])[0];
          setSchedule(updatedSchedule);
          updateOrAddInList(updatedSchedule);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to pause schedule"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to pause schedule"));
        return result;
      } finally {
        setIsPausing(false);
      }
    },
    [updateOrAddInList]
  );

  const resume = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Schedule ID is required");
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsResuming(true);
      setLastError(null);

      try {
        const result = await ExpenseScheduleService.resume(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedSchedule = (result.data as ExpenseSchedule[])[0];
          setSchedule(updatedSchedule);
          updateOrAddInList(updatedSchedule);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to resume schedule"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to resume schedule"));
        return result;
      } finally {
        setIsResuming(false);
      }
    },
    [updateOrAddInList]
  );

  const runNow = useCallback(
    async (id: string): Promise<ExpenseScheduleRunNowResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Schedule ID is required");
        setLastError(ApiError.fromResult(result));
        return result as ExpenseScheduleRunNowResult;
      }

      setIsRunningNow(true);
      setLastError(null);

      try {
        const result = await ExpenseScheduleService.runNow(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedSchedule = (result.data as ExpenseSchedule[])[0];
          setSchedule(updatedSchedule);
          updateOrAddInList(updatedSchedule);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to run schedule"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error) as ExpenseScheduleRunNowResult;
        setLastError(ApiError.fromException(error, "Failed to run schedule"));
        return result;
      } finally {
        setIsRunningNow(false);
      }
    },
    [updateOrAddInList]
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPage + 1;
    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ==========================================
  // Derived Data Helpers
  // ==========================================

  const getScheduleById = useCallback(
    (id: string | undefined): ExpenseSchedule | undefined => {
      if (!id) return undefined;
      return schedules.find((item) => item.id === id);
    },
    [schedules]
  );

  const activeSchedules = useMemo(
    () => schedules.filter((s) => s.status === EXPENSE_SCHEDULE_STATUS.ACTIVE),
    [schedules]
  );

  const pausedSchedules = useMemo(
    () => schedules.filter((s) => s.status === EXPENSE_SCHEDULE_STATUS.PAUSED),
    [schedules]
  );

  const completedSchedules = useMemo(
    () => schedules.filter((s) => s.status === EXPENSE_SCHEDULE_STATUS.COMPLETED),
    [schedules]
  );

  // ==========================================
  // Effects
  // ==========================================

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(
          initialFilterRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortByRef.current,
          true
        );
      }
      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(
          initialFilterRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortByRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      schedule,
      schedules,
      isLoading,
      isLoaded,
      isEmpty,

      // Pagination
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isCreating,
      isUpdating,
      removingId,
      isPausing,
      isResuming,
      isRunningNow,

      // Filter and sort
      filter,
      sortBy,

      // Core operations
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,

      // Custom operations
      pause,
      resume,
      runNow,

      // Convenience methods
      loadMore,
      refresh,

      // UI state management
      setSelectedSchedule: setSchedule,

      // Helper operations
      reset,

      // Lookup helpers
      getScheduleById,

      // Derived data helpers
      activeSchedules,
      pausedSchedules,
      completedSchedules,

      // Error handling
      lastError,
      clearError,
    }),
    [
      schedule,
      schedules,
      isLoading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      removingId,
      isPausing,
      isResuming,
      isRunningNow,
      filter,
      sortBy,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      pause,
      resume,
      runNow,
      loadMore,
      refresh,
      getScheduleById,
      activeSchedules,
      pausedSchedules,
      completedSchedules,
      lastError,
      clearError,
      reset,
    ]
  );
};